<script lang="ts">
import { defineComponent, ref, watch, onMounted } from 'vue'
import SuggestTags from '@/components/search/SuggestTags.vue'
import { useConditionalScreenLock } from '@/composables/useScreenLock'
import { useSearchWordStore } from '@/stores/searchWord'
import { useMediaQueryStore } from '@/stores/mediaQuery'

export default defineComponent({
  name: 'SearchWordWrapperForSP',

  components: {
    SuggestTags,
  },

  props: {
    isOpen: {
      type: Boolean,
      default: null,
    },
  },

  emits: ['update:isOpen', 'open', 'close'],

  setup(props, { emit }) {
    // store
    const searchWordStore = useSearchWordStore()
    const mediaQueryStore = useMediaQueryStore()

    const isOpenLocal = ref(false)

    // 検索ウインドウが開いている間はロックを持つ
    useConditionalScreenLock(() => isOpenLocal.value, {
      hasBlackOut: true,
      hasBlur: true,
      hasScrollLock: true,
    })

    watch(() => props.isOpen, (newValue) => {
      isOpenLocal.value = props.isOpen

      if (newValue) emit('open')
    })

    /**
     * ブラウザの拡縮等でレンダリング中に画面サイズが変わり、
     * SP 用検索ウインドウが開いている状態でタブレットサイズを割ったら強制的に非表示に切り替える
     */
    watch(() => mediaQueryStore.context.isUnderTablet, () => {
      if (!mediaQueryStore.context.isUnderTablet) closeWrapper()
    })

    onMounted(() => {
      if (props.isOpen !== null) {
        isOpenLocal.value = props.isOpen
      }
    })

    /**
     * @description 画面幅がタブレットの境界をまたぐと、開いていなくても呼ばれる（上の isUnderTablet の watch）。
     *              閉じているときに update:isOpen / close を emit しないようにガードする
     */
    const closeWrapper = () => {
      if (!isOpenLocal.value) return

      isOpenLocal.value = false
      emit('update:isOpen', isOpenLocal.value)
      emit('close')
    }

    return {
      // store
      searchWordStore,

      // data
      isOpenLocal,

      // methods
      closeWrapper,
    }
  }
})
</script>

<template>
  <transition name="is-slide__vertical">
    <section
      v-show="isOpenLocal"
      class="f-section__overlaySearchWord">
      <div class="f-section__overlaySearchWord--body">
        <div class="l-unit__searchWordWrapper">
          <div class="l-unit__searchWordWrapper--description is-search">
            <slot />
          <!-- /.l-unit__searchWordWrapper description --></div>

          <div class="l-unit__searchWordWrapper--description is-suggest">
            <dl
              v-if="searchWordStore.context.suggest.search.length"
              class="dl">
              <dt class="dt u-text__small u-text__bold">
                よく検索されるワード
              </dt>
              <dd class="dd">
                <SuggestTags :items="searchWordStore.context.suggest.search" />
              </dd>
            </dl>
            <dl
              v-if="searchWordStore.context.suggest.access.length"
              class="dl">
              <dt class="dt u-text__small u-text__bold">
                よく閲覧される用語
              </dt>
              <dd class="dd">
                <SuggestTags :items="searchWordStore.context.suggest.access" />
              </dd>
            </dl>
          <!-- /.l-unit__searchWordWrapper description --></div>

          <div class="l-unit__searchWordWrapper--action is-close">
            <div class="o-action__close">
              <button
                class="o-action__close--item"
                @click="closeWrapper">
                閉じる
              </button>
            <!-- /.o-action__close --></div>
          </div>
        <!-- /.l-unit__searchWordWrapper --></div>
      <!-- /.f-section__overlaySearchWord body --></div>
    <!-- /.f-section__overlaySearchWord --></section>
  </transition>
</template>

<style lang="sass" scoped>
// foundation
.f-section__overlaySearchWord
  width: 100%
  height: 100%
  position: fixed
  top: 0
  left: 0
  background-color: rgba($bg-white, 1)
  z-index: 20 // same z-index as screen lock.

// layout
.l-unit__searchWordWrapper
  $root: &
  display: grid
  grid-template:
    rows: auto 1fr
    columns: 1fr auto
  line-height: 1

  & &--description
    &.is-search
      grid:
        row: 1 / 2
        column: 1 / 2
      padding: 0.5em 0.75em
      border-bottom: 1px solid $border-x-light-gray

    &.is-suggest
      grid:
        row: 2 / 3
        column: 1 / 3
      padding: 1.5em 0.75em

  & &--action
    &.is-close
      grid:
        row: 1 / 2
        column: 2 / 3
      padding-right: 4px
      border-bottom: 1px solid $border-x-light-gray

  &.is-open
    visibility: visible
    opacity: 1

// object
.o-action__close
  $button-size: 40px
  height: 100%

  & &--item
    +iconCross($color: $border-x-dark-gray)
    width: $button-size
    height: 100%
</style>
